import "./styles.css";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent } from "@tiptap/react";

import { Icons } from "../Icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { TiptapContext } from "../../context/TipTapProvider";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

const getTableMenu = (editor) => [
  {
    id: 102,
    name: "Add Column Before",
    action: () => editor.chain().focus().addColumnBefore().run(),
  },
  {
    id: 103,
    name: "Add Column After",
    action: () => editor.chain().focus().addColumnAfter().run(),
  },
  {
    id: 104,
    name: "Delete Column",
    action: () => editor.chain().focus().deleteColumn().run(),
  },
  {
    id: 105,
    name: "Add Row Before",
    action: () => editor.chain().focus().addRowBefore().run(),
  },
  {
    id: 106,
    name: "Add Row After",
    action: () => editor.chain().focus().addRowAfter().run(),
  },
  {
    id: 107,
    name: "Delete Row",
    action: () => editor.chain().focus().deleteRow().run(),
  },
  {
    id: 108,
    name: "Delete Table",
    action: () => editor.chain().focus().deleteTable().run(),
  },
  {
    id: 109,
    name: "Merge Cells",
    action: () => editor.chain().focus().mergeCells().run(),
  },
  {
    id: 111,
    name: "Toggle Header Column",
    action: () => editor.chain().focus().toggleHeaderColumn().run(),
  },
  {
    id: 112,
    name: "Toggle Header Row",
    action: () => editor.chain().focus().toggleHeaderRow().run(),
  },
  {
    id: 113,
    name: "Toggle Header Cell",
    action: () => editor.chain().focus().toggleHeaderCell().run(),
  },
  {
    id: 114,
    name: "Merge Or Split",
    action: () => editor.chain().focus().mergeOrSplit().run(),
  },
  {
    id: 115,
    name: "Set Cell Attribute",
    action: () => editor.chain().focus().setCellAttribute("colspan", 2).run(),
  },
];

const MenuBarIcon = ({ editor }) => [
  {
    id: 1,
    name: "bold",
    icon: Icons.bold,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleBold().run();
    },
    disable: !editor.can().chain().focus().toggleBold().run(),
    isActive: editor.isActive("bold") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 2,
    name: "italic",
    icon: Icons.italic,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleItalic().run();
    },
    disable: !editor.can().chain().focus().toggleItalic().run(),
    isActive: editor.isActive("italic") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 21,
    name: "underline",
    icon: Icons.underline,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleUnderline().run();
    },
    disable: false,
    isActive: editor.isActive("underline") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 3,
    name: "strike",
    icon: Icons.strikethrough,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleStrike().run();
    },
    disable: !editor.can().chain().focus().toggleStrike().run(),
    isActive: editor.isActive("strike") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 4,
    name: "code",
    icon: Icons.code,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleCode().run();
    },
    disable: !editor.can().chain().focus().toggleCode().run(),
    isActive: editor.isActive("code") ? "is-active text-primary/70" : "",
    hover: false,
    split: true,
  },
  {
    id: 5,
    name: "heading1",
    icon: Icons.h1,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    },
    disable: false,
    isActive: editor.isActive("heading", { level: 1 })
      ? "is-active text-primary/70"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 6,
    name: "heading2",
    icon: Icons.h2,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    },
    disable: false,
    isActive: editor.isActive("heading", { level: 2 })
      ? "is-active text-primary/70"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 13,
    name: "heading3",
    icon: Icons.h3,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    },
    disable: false,
    isActive: editor.isActive("heading", { level: 3 })
      ? "is-active text-primary/70"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 14,
    name: "heading4",
    icon: Icons.h4,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHeading({ level: 4 }).run();
    },
    disable: false,
    isActive: editor.isActive("heading", { level: 4 })
      ? "is-active text-primary/70"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 15,
    name: "heading5",
    icon: Icons.h5,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHeading({ level: 5 }).run();
    },
    disable: false,
    isActive: editor.isActive("heading", { level: 5 })
      ? "is-active text-primary/70"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 7,
    name: "paragraph",
    icon: Icons.paragraph,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().setParagraph().run();
    },
    disable: false,
    isActive: editor.isActive("paragraph") ? "is-active text-primary/70" : "",
    hover: false,
    split: true,
  },
  {
    id: 8,
    name: "bullet list",
    icon: Icons.ul,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleBulletList().run();
    },
    disable: false,
    isActive: editor.isActive("bulletList")
      ? "is-active text-primary/70 list-disc"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 9,
    name: "ordered list",
    icon: Icons.ol,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleOrderedList().run();
    },
    disable: false,
    isActive: editor.isActive("orderedList")
      ? "is-active text-primary/70 list-decimal"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 16,
    name: "align left",
    icon: Icons.alignLeft,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().setTextAlign("left").run();
    },
    disable: false,
    isActive: editor.isActive({ textAlign: "left" }) ? "is-active" : "",
    hover: false,
    split: false,
  },
  {
    id: 17,
    name: "align center",
    icon: Icons.alignCenter,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().setTextAlign("center").run();
    },
    disable: false,
    isActive: editor.isActive({ textAlign: "center" })
      ? "is-active text-primary/70 text-center"
      : "",
    hover: false,
    split: false,
  },
  {
    id: 18,
    name: "align right",
    icon: Icons.alignRight,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().setTextAlign("right").run();
    },
    disable: false,
    isActive: editor.isActive({ textAlign: "right" }) ? "is-active" : "",
    hover: false,
    split: false,
  },
  {
    id: 19,
    name: "align justify",
    icon: Icons.alignJustify,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().setTextAlign("justify").run();
    },
    disable: false,
    isActive: editor.isActive({ textAlign: "justify" }) ? "is-active" : "",
    hover: false,
    split: true,
  },
  {
    id: 20,
    name: "highlight",
    icon: Icons.bg,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleHighlight().run();
    },
    disable: false,
    isActive: editor.isActive("highlight") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 22,
    name: "link",
    icon: Icons.link,
    onClick: (e) => {
      e?.stopPropagation();
      const url = window.prompt("Enter URL:");
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    },
    disable: false,
    isActive: editor.isActive("link") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 23,
    name: "unlink",
    icon: Icons.unlink,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().unsetLink().run();
    },
    disable: !editor.isActive("link"),
    isActive: "",
    hover: false,
    split: true,
  },
  {
    id: 24,
    name: "subscript",
    icon: Icons.subscript,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleSubscript().run();
    },
    disable: false,
    isActive: editor.isActive("subscript") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 25,
    name: "superscript",
    icon: Icons.superscript,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleSuperscript().run();
    },
    disable: false,
    isActive: editor.isActive("superscript") ? "is-active text-primary/70" : "",
    hover: false,
    split: true,
  },
  {
    id: 26,
    name: "task list",
    icon: Icons.taskList,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleTaskList().run();
    },
    disable: false,
    isActive: editor.isActive("taskList") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 27,
    name: "details",
    icon: Icons.details,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleDetails().run();
    },
    disable: false,
    isActive: editor.isActive("details") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 28,
    name: "youtube",
    icon: Icons.youtube,
    onClick: (e) => {
      e?.stopPropagation();
      const url = window.prompt("Enter YouTube URL:");
      if (url) {
        editor.chain().focus().setYoutubeVideo({ src: url }).run();
      }
    },
    disable: false,
    isActive: "",
    hover: false,
    split: false,
  },
  {
    id: 29,
    name: "Font Size",
    icon: Icons.text,
    hover: true,
    split: false,
  },
  {
    id: 30,
    name: "Font Family",
    icon: Icons.fontFamily,
    hover: true,
    split: false,
  },
  {
    id: 31,
    name: "Background Color",
    icon: Icons.palette,
    hover: true,
    split: true,
  },
  {
    id: 38,
    name: "Styled Container",
    icon: Icons.container || Icons.box,
    hover: true,
    split: false,
  },
  {
    id: 32,
    name: "code block",
    icon: Icons.codeblock,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleCodeBlock().run();
    },
    disable: false,
    isActive: editor.isActive("codeBlock") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 33,
    name: "blockquote",
    icon: Icons.blockquote,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().toggleBlockquote().run();
    },
    disable: false,
    isActive: editor.isActive("blockquote") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 34,
    name: "undo",
    icon: Icons.undo,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().undo().run();
    },
    disable: !editor.can().undo(),
    isActive: editor.isActive("table") ? "is-active text-primary/70" : "",
    hover: false,
    split: false,
  },
  {
    id: 35,
    name: "redo",
    icon: Icons.redo,
    onClick: (e) => {
      e?.stopPropagation();
      editor.chain().focus().redo().run();
    },
    disable: !editor.can().redo(),
    isActive: editor.isActive("table") ? "is-active text-primary/70" : "",
    hover: false,
    split: true,
  },

  {
    id: 36,
    name: "insert table",
    icon: Icons.table,
    onClick: (e) => {
      e?.stopPropagation();
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
    disable: false,
    isActive: editor.isActive("table") ? "is-active text-primary/70" : "",
    hover: false,
  },
  {
    id: 37,
    name: "Edit Table",
    icon: Icons.tableMenu,
    hover: true,
    split: true,
  },
];

function MenuBar({ setImageURL, className }) {
  const { t } = useTranslation();
  const { editor } = useContext(TiptapContext);
  const fileInputRef = useRef(null);

  const tableMenu = useMemo(
    () => (editor ? getTableMenu(editor) : []),
    [editor]
  );

  const fontSizes = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "48px"];
  const fontFamilies = [
    { label: "Default", value: "" },
    { label: "Arial", value: "Arial, sans-serif" },
    { label: "Times New Roman", value: "'Times New Roman', serif" },
    { label: "Courier New", value: "'Courier New', monospace" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Verdana", value: "Verdana, sans-serif" },
    { label: "Comic Sans", value: "'Comic Sans MS', cursive" },
  ];

  if (!editor) {
    return null;
  }
  const MenuBarIconValue = MenuBarIcon({ editor });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        toast.error(t("image_size_error"));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setImageURL(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={cn(
        className,
        "flex flex-wrap gap-1 bg-gray-800 rounded-t-2xl p-2 text-xs text-white w-full"
      )}
    >
      <input
        type="color"
        className={"w-7 h-6"}
        onInput={(event) =>
          editor.chain().focus().setColor(event.target.value).run()
        }
        onClick={(e) => e.stopPropagation()}
        value={editor.getAttributes("textStyle").color}
      />
      {MenuBarIconValue.map((item) =>
        item.hover ? (
          <div className="flex items-center" key={item.id}>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={item.disable}
                className={`hover:bg-gray-500 text-xs hover:rounded-lg p-1 ${
                  item.disable
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
              >
                <item.icon size={15} />
              </DropdownMenuTrigger>

              <DropdownMenuContent
                side="bottom"
                align="start"
                style={{ zIndex: 2000 }}
                className="max-h-64 overflow-y-auto"
              >
                {item.name === "Edit Table" &&
                  tableMenu.map((menuItem) => (
                    <DropdownMenuItem
                      key={menuItem.id}
                      onClick={menuItem.action}
                      disabled={!editor.isActive("table")}
                    >
                      {menuItem.name}
                    </DropdownMenuItem>
                  ))}

                {item.name === "Font Size" &&
                  fontSizes.map((size) => (
                    <DropdownMenuItem
                      key={size}
                      onClick={() => editor.chain().focus().setFontSize(size).run()}
                    >
                      <span style={{ fontSize: size }}>{size}</span>
                    </DropdownMenuItem>
                  ))}

                {item.name === "Font Family" &&
                  fontFamilies.map((font) => (
                    <DropdownMenuItem
                      key={font.value || "default"}
                      onClick={() =>
                        font.value
                          ? editor.chain().focus().setFontFamily(font.value).run()
                          : editor.chain().focus().unsetFontFamily().run()
                      }
                    >
                      <span style={{ fontFamily: font.value || "inherit" }}>
                        {font.label}
                      </span>
                    </DropdownMenuItem>
                  ))}

                {item.name === "Background Color" && (
                  <>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().unsetBackgroundColor().run()}
                    >
                      Clear Background
                    </DropdownMenuItem>
                    <div className="px-2 py-1">
                      <input
                        type="color"
                        className="w-full h-8 cursor-pointer"
                        onChange={(e) =>
                          editor.chain().focus().setBackgroundColor(e.target.value).run()
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </>
                )}

                {item.name === "Styled Container" && (
                  <>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("info").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)", border: "1px solid #60A5FA" }} />
                        <span>Info</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("success").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)", border: "1px solid #4ADE80" }} />
                        <span>Success</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("warning").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)", border: "1px solid #FBBF24" }} />
                        <span>Warning</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("question").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", border: "1px solid #818CF8" }} />
                        <span>Question</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("highlight").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #FEF9C3 0%, #FDE047 40%)", border: "1px solid #FACC15" }} />
                        <span>Highlight</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("gradient").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 50%, #F3E8FF 100%)", border: "1px solid #D8B4FE" }} />
                        <span>Gradient</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("quote").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: "#F9FAFB", borderLeft: "3px solid #6B7280" }} />
                        <span>Quote</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setStyledContainer("minimal").run()}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ border: "1px solid #E5E7EB" }} />
                        <span>Minimal</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().unsetStyledContainer().run()}
                      className="border-t mt-1 pt-1"
                    >
                      Remove Container
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {item.split && (
              <div className="mx-1 w-[1px] flex bg-gray-500 h-4" />
            )}
          </div>
        ) : (
          <div className="flex items-center h-full gap-1" key={item.id}>
            <button
              onClick={(e) => {
                item.onClick(e);
              }}
              disabled={item.disable}
              className={`${
                item.disable
                  ? "cursor-not-allowed p-1"
                  : "cursor-pointer hover:bg-gray-500 hover:rounded-lg p-1"
              } + ${item.isActive ? item.isActive : ""}`}
            >
              <item.icon size={15} />
            </button>
            {item.split && (
              <div className="mx-1 w-[1px] flex bg-gray-500 h-4" />
            )}
          </div>
        )
      )}
      <div className="cursor-pointer hover:bg-gray-500 hover:rounded-lg p-1 max-h-3.5 ">
        <input
          type="file"
          onChange={handleImageChange}
          ref={fileInputRef}
          className="hidden"
        />
        <Icons.image
          size={15}
          onClick={(e) => {
            e.stopPropagation();
            handleIconClick();
          }}
        />
      </div>
    </div>
  );
}

function Tiptap({ onUpdate, value, defaultValue, hideMenu }) {
  const { editor } = useContext(TiptapContext);
  const [imageURL, setImageURL] = useState(null);

  useEffect(() => {
    if (editor && imageURL) {
      editor.commands.setImage({
        src: imageURL,
      });
    }
  }, [imageURL]);

  editor.on("create", ({ editor }) => {
    editor.commands.setContent(value ? value : defaultValue);
  });

  editor.on("update", ({ editor }) => {
    onUpdate(editor.getHTML());
  });

  return (
    <div className="relative">
      {!hideMenu && (
        <MenuBar
          className="sticky top-0 z-50"
          editor={editor}
          setImageURL={setImageURL}
        />
      )}
      <EditorContent
        className="w-full p-3 max-h-[200px] overflow-y-auto border min-h-[200px]"
        style={{ textAlign: 'start' }}
        editor={editor}
      />
    </div>
  );
}

export default Tiptap;
